import { useEffect, useState } from "react";
import api from "../../lib/api";
import Input from "../ui/Input";
import PhoneInput from "../PhoneInput";
import { COUNTRIES, RWANDA_CITIES, dialCodeFor } from "../../lib/locations";

const SELECT =
  "flex h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

export const EMPTY_SHOP = {
  business_type: "business",
  name: "",
  known_name: "",
  tin: "",
  category_id: "",
  work_mode: "on_site",
  country: "Rwanda",
  city: "",
  address: "",
  whatsapp_number: "",
  payout_phone: "",
};

// What the API takes: other numbers only when the owner gave them, so the
// server uses their own phone for the shop, its WhatsApp and payouts.
export function shopPayload(shop, { whatsappDiffers, payoutDiffers }) {
  const isBusiness = shop.business_type === "business";
  return {
    ...shop,
    name: shop.name.trim(),
    known_name: shop.known_name || undefined,
    tin: isBusiness && shop.tin ? shop.tin : undefined,
    category_id: shop.category_id || undefined,
    whatsapp_number: whatsappDiffers && shop.whatsapp_number ? shop.whatsapp_number : undefined,
    payout_phone: payoutDiffers && shop.payout_phone ? shop.payout_phone : undefined,
  };
}

/**
 * The shop's details, asked once — on the sign-up form, or on the
 * dashboard for an account that has no shop yet. The owner's phone number
 * is also the shop's WhatsApp and payout number unless they tick otherwise.
 */
export default function ShopDetailsFields({ shop, onChange, phone, whatsappDiffers, setWhatsappDiffers, payoutDiffers, setPayoutDiffers }) {
  const [categories, setCategories] = useState([]);
  const [nameTaken, setNameTaken] = useState(false);
  const isBusiness = shop.business_type === "business";
  const isRwanda = shop.country === "Rwanda";
  const dial = dialCodeFor(shop.country);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  const set = (field) => (e) => onChange({ ...shop, [field]: e.target.value });

  const checkName = async () => {
    if (!shop.name.trim()) return setNameTaken(false);
    try {
      const { data } = await api.get("/business-name-available", { params: { name: shop.name.trim() } });
      setNameTaken(!data.available);
    } catch {
      setNameTaken(false);
    }
  };

  return (
    <>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Account type</label>
        <select value={shop.business_type} onChange={set("business_type")} className={SELECT}>
          <option value="business">Business</option>
          <option value="freelance">Freelancer / independent professional</option>
        </select>
      </div>
      <div>
        <Input
          placeholder={isBusiness ? "Business name" : "Your name or brand, as customers will see it"}
          value={shop.name}
          onChange={(e) => {
            setNameTaken(false);
            set("name")(e);
          }}
          onBlur={checkName}
          aria-invalid={nameTaken}
          required
        />
        {nameTaken && (
          <p className="mt-1 text-xs text-destructive">
            This name is already taken on SHUWA. Please choose a different one — for example, add your area or street.
          </p>
        )}
      </div>
      {isBusiness && (
        <>
          <Input
            placeholder="TIN (9-digit Tax Identification Number, optional)"
            value={shop.tin}
            onChange={(e) => onChange({ ...shop, tin: e.target.value.replace(/\D/g, "").slice(0, 9) })}
            inputMode="numeric"
            maxLength={9}
          />
          <Input placeholder="Known name (if different, optional)" value={shop.known_name} onChange={set("known_name")} />
        </>
      )}
      <select value={shop.category_id} onChange={set("category_id")} className={SELECT} aria-label="Category" required>
        <option value="">What do you do? (category)</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">How do you work?</label>
        <select value={shop.work_mode} onChange={set("work_mode")} className={SELECT}>
          <option value="on_site">On site</option>
          <option value="remote">Remote</option>
          <option value="hybrid">Hybrid</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <select
          value={shop.country}
          onChange={(e) => onChange({ ...shop, country: e.target.value, city: "" })}
          className={SELECT}
          aria-label="Country"
          required
        >
          {COUNTRIES.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        {isRwanda ? (
          <select value={shop.city} onChange={set("city")} className={SELECT} aria-label="City" required>
            <option value="">City / town</option>
            {RWANDA_CITIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        ) : (
          <Input placeholder="City" value={shop.city} onChange={set("city")} required />
        )}
      </div>
      <Input placeholder="Street address (e.g. KN 4 Ave, near …)" value={shop.address} onChange={set("address")} />

      <div className="space-y-2 rounded-xl bg-muted/50 p-3 text-sm text-slate-700">
        <p className="text-xs text-muted-foreground">
          {phone
            ? <>Your number <span className="font-medium text-slate-900">{phone}</span> is used for your shop, WhatsApp and payouts.</>
            : "The phone number below is used for your shop, WhatsApp and payouts."}
        </p>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
            checked={whatsappDiffers}
            onChange={(e) => setWhatsappDiffers(e.target.checked)}
          />
          My WhatsApp is on a different number
        </label>
        {whatsappDiffers && (
          <PhoneInput dial={dial} value={shop.whatsapp_number} onChange={(v) => onChange({ ...shop, whatsapp_number: v })} aria-label="WhatsApp number" required />
        )}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
            checked={payoutDiffers}
            onChange={(e) => setPayoutDiffers(e.target.checked)}
          />
          I get paid (mobile money) on a different number
        </label>
        {payoutDiffers && (
          <PhoneInput dial={dial} value={shop.payout_phone} onChange={(v) => onChange({ ...shop, payout_phone: v })} aria-label="Mobile money payout number" required />
        )}
      </div>
    </>
  );
}
